import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download } from 'lucide-react';
import CommentPanel from '../components/CommentPanel';
import ReviewStage from '../components/ReviewStage';
import BreadcrumbBar from '../ui/BreadcrumbBar';
import Button from '../ui/Button';
import ErrorState from '../ui/ErrorState';
import Input from '../ui/Input';
import Skeleton from '../ui/Skeleton';
import Stage from '../ui/Stage';
import { useToast } from '../ui/Toast';
import { TierToggle } from '../shell/ReviewShell';
import { clean } from '../shell/copy';
import { API_BASE } from '../lib/apiBase';

// Video share viewer (/share/:token), anonymous guests: the same review frame as /review/:id (DESIGN rev 2.1 §2.6, §3.3,
// share-A.html) with no rail and no back. A guest is asked exactly what the API needs, name and email, once, in the
// composer's place; then the same guest-session call, and the composer. The session lasts for the browser session
// (sessionStorage, as before; the guest token itself expires after 24 hours). Calls, unchanged:
//   GET  /api/share/:token                  link settings, asset, comments (again after each guest comment)
//   POST /api/share/:token/guest-session    { name, email }             → { guestToken }
//   POST /api/share/:token/comment          { timestampSeconds, text }  Authorization: Bearer <guestToken>
//   GET  /api/share/:token/download         → { url } (when the link allows downloads)

const SESSION_KEY = 'jv_guest_session';

// The guest's one question, in the composer's place: name and email, then Continue.
function GuestStep({ token, onSuccess }) {
  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim())  { setError('Add your name so the team knows who wrote the comment.'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Add a valid email address so the team can reply.'); return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/api/share/${token}/guest-session`, {
        name: name.trim(), email: email.trim(),
      });
      onSuccess({ name: name.trim(), email: email.trim(), guestToken: res.data.guestToken });
    } catch {
      setError('Your details were not saved. Check the connection and try again.');
      setSubmitting(false);
    }
  }

  return (
    <form className="ui ui-composer" onSubmit={handleSubmit} aria-label="Your details" noValidate>
      <div className="ui-composer__id">
        <Input label="Your name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" data-testid="guest-name" />
        <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" data-testid="guest-email" />
      </div>
      {error && <span className="ui-field__error" role="alert">{error}</span>}
      <div className="ui-composer__tools">
        <span className="ui-composer__note">Your name shows next to your comments. Asked once per visit.</span>
        <Button type="submit" variant="primary" loading={submitting} data-testid="guest-continue">Continue</Button>
      </div>
    </form>
  );
}

export default function ShareLinkViewer() {
  const { token } = useParams();
  const toast     = useToast();
  const seekRef   = useRef(null);
  const playerApi = useRef(null);

  const [data,               setData]               = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [inactive,           setInactive]           = useState(false);
  const [failed,             setFailed]             = useState(false);
  const [comments,           setComments]           = useState([]);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [activeCommentId,    setActiveCommentId]    = useState(null);
  const [commentsHidden,     setCommentsHidden]     = useState(false);

  const [guestSession, setGuestSession] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)); }
    catch { return null; }
  });

  const fetchData = useCallback(async () => {
    setFailed(false);
    try {
      const res = await axios.get(`${API_BASE}/api/share/${token}`);
      setData(res.data);
      setComments(res.data.comments || []);
    } catch (err) {
      if (err.response?.status === 404) setInactive(true);
      else setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function refreshComments() {
    try {
      const res = await axios.get(`${API_BASE}/api/share/${token}`);
      setComments(res.data.comments || []);
    } catch { /* the list stays as it was */ }
  }

  async function handleGuestCommentPost({ timestampSeconds, text }) {
    await axios.post(
      `${API_BASE}/api/share/${token}/comment`,
      { timestampSeconds, text },
      { headers: { Authorization: `Bearer ${guestSession.guestToken}` } },
    );
  }

  function handleDownload() {
    axios.get(`${API_BASE}/api/share/${token}/download`)
      .then(res => window.open(res.data.url, '_blank'))
      .catch(() => toast('The download did not start. Try again.', { tone: 'danger' }));
  }

  const markers = useMemo(() => comments.filter(c => !c.parentId).map(c => ({
    id: c.id, at: Number(c.timestampSeconds), label: `Comment by ${clean(c.commenter?.name || c.commenterName || 'Unknown')}`,
  })), [comments]);

  if (loading) {
    return (
      <div className="ui ui-review ui-review--fit" aria-busy="true">
        <BreadcrumbBar crumbs={['Shared video']} actions={<TierToggle />} />
        <Stage label="Loading video"><Skeleton variant="block" label="Loading video" /></Stage>
        <aside className="ui ui-comments" aria-label="Comments">
          <div className="ui-comments__list">{[0, 1, 2].map(i => <Skeleton key={i} variant="block" height="84px" />)}</div>
        </aside>
      </div>
    );
  }

  if (inactive || failed || !data) {
    return (
      <div className="ui ui-review ui-review--fit ui-review--no-comments">
        <BreadcrumbBar crumbs={['Shared video']} actions={<TierToggle />} />
        <div className="ui-review-msg">
          {failed
            ? <ErrorState title="The video did not load" message="Check your connection and try again." onRetry={() => { setLoading(true); fetchData(); }} />
            : (
              <>
                <h1 className="ui-heading">This link is no longer active</h1>
                <p className="ui-p ui-text-2">The share link was turned off or has expired. Ask the person who sent it for a new one.</p>
              </>
            )}
        </div>
      </div>
    );
  }

  const { shareLink, videoAsset, clientName } = data;
  const allowComments = !!shareLink.allowComments;
  const slot = !allowComments
    ? <div className="ui ui-composer"><p className="ui-composer__note">Comments are turned off for this link.</p></div>
    : !guestSession
      ? (
        <GuestStep token={token} onSuccess={session => {
          setGuestSession(session);
          try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* private mode: asked again next visit */ }
        }} />
      )
      : null;

  return (
    <div className={commentsHidden ? 'ui ui-review ui-review--fit ui-review--no-comments' : 'ui ui-review ui-review--fit'}>
      <BreadcrumbBar crumbs={[clean(clientName), clean(videoAsset.fileName)].filter(Boolean)}
        actions={(
          <>
            {shareLink.allowDownload && <Button variant="secondary" icon={Download} onClick={handleDownload}>Download</Button>}
            <TierToggle />
          </>
        )}
        commentsHidden={commentsHidden} onToggleComments={() => setCommentsHidden(h => !h)} />
      <ReviewStage
        src={videoAsset.playbackUrl || videoAsset.cloudfrontUrl}
        poster={videoAsset.thumbnailUrl || undefined}
        label={clean(videoAsset.fileName)}
        comments={comments}
        markers={markers}
        activeMarker={activeCommentId}
        onMarker={m => setActiveCommentId(m.id)}
        seekRef={seekRef}
        onTimeUpdate={setCurrentTimeSeconds}
        playerRef={playerApi}
      />
      {!commentsHidden && (
        <CommentPanel
          videoAssetId={videoAsset.id}
          currentTimeSeconds={currentTimeSeconds}
          onSeekTo={(secs, id) => { seekRef.current?.(secs); if (id) setActiveCommentId(id); }}
          comments={comments}
          onCommentsChange={refreshComments}
          activeCommentId={activeCommentId}
          canPost={allowComments && !!guestSession}
          identity={guestSession ? { name: guestSession.name } : null}
          onCommentPost={handleGuestCommentPost}
          slot={slot}
          manage={false}
          onTypingStart={() => playerApi.current?.pause()}
          details={(
            <dl className="ui-kv">
              <dt>File</dt><dd>{clean(videoAsset.fileName)}</dd>
              {clientName && <><dt>Client</dt><dd>{clean(clientName)}</dd></>}
            </dl>
          )}
          emptyMessage={allowComments ? undefined : 'No comments on this video.'}
        />
      )}
    </div>
  );
}
